// 15×15 mesh-grid board overlay, ported pixel-for-pixel from ludo_client.php.
// Each cell is a grid item; tokens are absolute-positioned circles rendered
// into the exact cell their (colour, pos) maps to via trackCoords/homeCoords.
//
// The board is wrapped in .board-rotation-wrapper with --board-rot set to
// match the local player's colour, so the player's yard is always at the
// bottom-left regardless of which seat the server assigned them.

import { CSSProperties } from 'react';
import {
  Color,
  TRACK_COORDS,
  HOME_COORDS,
  COLOR_CONFIG,
  BOARD_ROTATION,
} from './board';

export type BoardToken = {
  color: Color;
  idx: number;           // 0..3 — within this player's 4 tokens
  pos: number;           // -1 yard, 0..50 track, 51..55 stretch, 56 home
};

export type PlayerState = {
  color: Color;
  tokens: number[];
  displayName?: string;
  avatarUrl?: string | null;
  isBot?: boolean;
};

// Short-lived visual effects placed by the Ludo page on top of the board.
//  - `kind: 'skull'` spawns a floating 💀 at cell (r,c) running the
//    floatUpFade keyframe — used when a token is captured there.
export type BoardFx = { id: number; kind: 'skull'; r: number; c: number };

// Per-token decoration that doesn't change token position — just adds
// a class (e.g. `just-landed`, `winning`) for a short window. Keyed by
// `${color}-${idx}` so a token can only carry one decoration at a time.
export type TokenDeco = Record<string, 'just-landed' | 'winning'>;

export type BoardProps = {
  tokens: BoardToken[];
  yourColor: Color | null;
  currentTurn: Color | null;
  validTokens: number[];
  onPickToken: (color: Color, idx: number) => void;
  fx?: BoardFx[];
  deco?: TokenDeco;
};

// Home-stacking micro offsets (percent of cell size) — matches PHP homeOffsets.
const HOME_BASE: Record<Color, { bx: number; by: number }> = {
  red:    { bx: -130, by: 0 },
  green:  { bx: 0,    by: -130 },
  yellow: { bx: 130,  by: 0 },
  blue:   { bx: 0,    by: 130 },
};
const HOME_MICRO: [number, number][] = [
  [0, -50], [50, 0], [0, 50], [-50, 0],
];

// Build the 15×15 mesh grid, skipping yard quadrants and the center 3×3
// (those are rendered as separate <div class="yard">/<div class="center-home">).
const MESH_CELLS: [number, number][] = (() => {
  const out: [number, number][] = [];
  for (let r = 1; r <= 15; r++) {
    for (let c = 1; c <= 15; c++) {
      if ((r <= 6 && c <= 6) || (r <= 6 && c >= 10) || (r >= 10 && c >= 10) || (r >= 10 && c <= 6)) continue;
      if (r >= 7 && r <= 9 && c >= 7 && c <= 9) continue;
      out.push([r, c]);
    }
  }
  return out;
})();

type Placement =
  | { kind: 'yard'; color: Color; slot: number }
  | { kind: 'cell'; r: number; c: number }
  | { kind: 'home' };

function placementFor(color: Color, pos: number, tokenIdx: number): Placement {
  if (pos === -1) return { kind: 'yard', color, slot: tokenIdx };
  if (pos <= 50) {
    const abs = (COLOR_CONFIG[color].startIndex + pos) % 52;
    const [r, c] = TRACK_COORDS[abs];
    return { kind: 'cell', r, c };
  }
  if (pos <= 55) {
    const [r, c] = HOME_COORDS[color][pos - 51];
    return { kind: 'cell', r, c };
  }
  return { kind: 'home' };
}

function group(tokens: BoardToken[]) {
  const byCell = new Map<string, BoardToken[]>();
  const byYard = new Map<string, BoardToken[]>();
  const home: BoardToken[] = [];
  for (const t of tokens) {
    const p = placementFor(t.color, t.pos, t.idx);
    if (p.kind === 'cell') {
      const k = `${p.r}-${p.c}`;
      const arr = byCell.get(k) || [];
      arr.push(t);
      byCell.set(k, arr);
    } else if (p.kind === 'yard') {
      const k = `${p.color}-${p.slot}`;
      const arr = byYard.get(k) || [];
      arr.push(t);
      byYard.set(k, arr);
    } else {
      home.push(t);
    }
  }
  return { byCell, byYard, home };
}

function TokenDot({
  t,
  onClick,
  isValid,
  style,
  className,
  deco,
}: {
  t: BoardToken;
  onClick: (c: Color, i: number) => void;
  isValid: boolean;
  style?: CSSProperties;
  className?: string;
  deco?: TokenDeco[string];
}) {
  const decoCls = deco ? ` ${deco}` : '';
  return (
    <div
      className={`token ${t.color}${isValid ? ' valid-move' : ''}${className ? ' ' + className : ''}${decoCls}`}
      style={style}
      onClick={isValid ? () => onClick(t.color, t.idx) : undefined}
    />
  );
}

// Render N tokens inside one cell, applying the clustering micro-offsets
// copied from PHP renderTokens() so overlapping tokens remain legible.
function renderClustered(
  tokens: BoardToken[],
  validTokens: number[],
  yourColor: Color | null,
  currentTurn: Color | null,
  onPickToken: (c: Color, i: number) => void,
  deco?: TokenDeco,
): JSX.Element[] {
  const count = tokens.length;
  return tokens.map((t, idx) => {
    const isValid =
      t.color === yourColor && currentTurn === yourColor && validTokens.includes(t.idx);
    let tx = 0, ty = 0, ts = 1;
    if (count > 1) {
      const base = 30;
      ts = count >= 4 ? 0.55 : 0.65;
      if (count === 2) {
        tx = idx === 0 ? -base : base;
        ty = idx === 0 ? -base : base;
      } else if (count === 3) {
        tx = idx === 0 ? 0 : idx === 1 ? -base : base;
        ty = idx === 0 ? -base : base;
      } else {
        const corner = idx % 4;
        const shift = Math.floor(idx / 4) * 15;
        tx = (corner === 0 || corner === 2 ? -base : base) + shift;
        ty = (corner === 0 || corner === 1 ? -base : base) + shift;
      }
    }
    const style: CSSProperties & Record<string, string | number> = {
      ['--t-x']: `${tx}%`,
      ['--t-y']: `${ty}%`,
      ['--t-s']: `${ts}`,
      zIndex: 10 + idx,
    };
    return (
      <TokenDot
        key={`${t.color}-${t.idx}`}
        t={t}
        onClick={onPickToken}
        isValid={isValid}
        style={style}
        className={count > 1 ? 'stacked' : ''}
        deco={deco?.[`${t.color}-${t.idx}`]}
      />
    );
  });
}

function renderHomeTokens(
  tokens: BoardToken[],
  onPickToken: (c: Color, i: number) => void,
  deco?: TokenDeco,
): JSX.Element[] {
  const perColor: Record<Color, number> = { red: 0, green: 0, yellow: 0, blue: 0 };
  return tokens.map((t) => {
    const cIdx = perColor[t.color]++;
    const base = HOME_BASE[t.color];
    const mx = HOME_MICRO[cIdx % 4][0];
    const my = HOME_MICRO[cIdx % 4][1];
    const style: CSSProperties & Record<string, string | number> = {
      ['--t-x']: `${base.bx + mx}%`,
      ['--t-y']: `${base.by + my}%`,
      ['--t-s']: '0.65',
      zIndex: 10 + cIdx,
    };
    return (
      <TokenDot
        key={`home-${t.color}-${t.idx}`}
        t={t}
        onClick={onPickToken}
        isValid={false}
        style={style}
        deco={deco?.[`${t.color}-${t.idx}`]}
      />
    );
  });
}

export default function Board({
  tokens, yourColor, currentTurn, validTokens, onPickToken, fx, deco,
}: BoardProps) {
  const { byCell, byYard, home } = group(tokens);
  const rot = BOARD_ROTATION[yourColor || 'blue'];
  // Group FX overlays by cell so each cell renders its own skulls.
  const fxByCell = new Map<string, BoardFx[]>();
  for (const f of fx || []) {
    const k = `${f.r}-${f.c}`;
    const arr = fxByCell.get(k) || [];
    arr.push(f);
    fxByCell.set(k, arr);
  }

  return (
    <div className="board-wrapper flex items-center justify-center z-10 my-0 w-full">
      <div
        className="board-rotation-wrapper slim_goldern_border"
        id="board-container"
        style={{ ['--board-rot' as any]: `${rot}deg` }}
      >
        <img
          id="ludo-bg-image"
          src="/games/ludo/LudoBoard_BG.png"
          className="absolute inset-0 w-full h-full object-cover z-0"
          alt=""
        />
        <div className="board-container-grid" id="board">
          {(['red', 'green', 'yellow', 'blue'] as Color[]).map((c) => (
            <div key={c} className={`yard yard-${c}`}>
              <div className="yard-inner">
                {[0, 1, 2, 3].map((slot) => {
                  const yardTokens = byYard.get(`${c}-${slot}`) || [];
                  return (
                    <div key={slot} className="yard-spot" id={`yard-${c}-${slot}`}>
                      {yardTokens.map((t) => {
                        const isValid =
                          t.color === yourColor &&
                          currentTurn === yourColor &&
                          validTokens.includes(t.idx);
                        return (
                          <TokenDot
                            key={`yard-${t.color}-${t.idx}`}
                            t={t}
                            onClick={onPickToken}
                            isValid={isValid}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="center-home" id="center-home">
            <div className="center-triangle">{renderHomeTokens(home, onPickToken, deco)}</div>
          </div>

          {MESH_CELLS.map(([r, c]) => {
            const cellTokens = byCell.get(`${r}-${c}`) || [];
            const cellFx = fxByCell.get(`${r}-${c}`) || [];
            return (
              <div
                key={`${r}-${c}`}
                className="cell"
                id={`cell-${r}-${c}`}
                style={{ gridRow: r, gridColumn: c }}
              >
                {renderClustered(
                  cellTokens,
                  validTokens,
                  yourColor,
                  currentTurn,
                  onPickToken,
                  deco,
                )}
                {cellFx.map((f) => (
                  <span key={f.id} className="skull-anim">💀</span>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
